import { hotTags, chatter } from "@/mocks/discussion";
import { toneSolid } from "./tones";

export default function Hero({
  query,
  onSearch,
  onPickTag,
}: {
  query: string;
  onSearch: (v: string) => void;
  onPickTag: (tag: string) => void;
}) {
  return (
    <section id="top" className="relative overflow-hidden bg-background-50">
      {/* 装饰背景 */}
      <div className="pointer-events-none absolute inset-0 select-none" aria-hidden="true">
        <div className="absolute -left-24 -top-24 h-72 w-72 rounded-full bg-primary-500/10 blur-2xl"></div>
        <div className="absolute right-0 top-10 h-80 w-80 rounded-full bg-secondary-500/15 blur-2xl"></div>
        <div className="absolute bottom-0 left-1/3 h-64 w-64 rounded-full bg-accent-500/10 blur-2xl"></div>
        <i className="ri-water-flash-line anim-float absolute left-[8%] top-24 text-5xl text-primary-300/40"></i>
        <i className="ri-cake-3-line anim-float absolute right-[12%] top-28 text-6xl text-secondary-400/40" style={{ animationDelay: "0.8s" }}></i>
        <i className="ri-fire-line absolute bottom-16 left-[14%] text-5xl text-accent-400/30"></i>
      </div>

      <div className="relative mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-10 px-4 pb-14 pt-12 md:px-6 md:pb-20 md:pt-16 lg:grid-cols-[1.1fr_0.9fr]">
        <div className="relative z-10">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-100 px-3 py-1.5 text-xs font-medium text-primary-700">
            <i className="ri-flashlight-line text-sm" aria-hidden="true"></i>
            全网热议 · 实时第一现场
          </span>

          <h1 className="mt-5 text-3xl font-black leading-tight text-foreground-950 md:text-5xl">
            吃瓜群众已就位，
            <span className="text-primary-500">热门话题</span>都在这里聊
          </h1>

          <p className="mt-4 max-w-xl text-base leading-relaxed text-foreground-600 md:text-lg">
            爆料网汇聚明星八卦、影视综艺、社会热点与科技趣闻。网友实时开麦，
            点开就能参与讨论、看实时反应，最新最热的话题评论区等你来占领。
          </p>

          {/* 搜索框 */}
          <form
            className="mt-7 flex w-full max-w-xl flex-col gap-3 sm:flex-row"
            onSubmit={(e) => {
              e.preventDefault();
              onSearch(query.trim());
            }}
          >
            <label className="flex flex-1 items-center gap-2 rounded-full border border-background-200 bg-background-100 px-5 py-3 text-foreground-700 transition-colors focus-within:border-primary-300">
              <i className="ri-search-line text-lg text-foreground-400" aria-hidden="true"></i>
              <input
                type="text"
                value={query}
                onChange={(e) => onSearch(e.target.value)}
                placeholder="搜搜最近大家在热议什么…"
                className="w-full bg-transparent text-sm outline-none placeholder:text-foreground-400"
                aria-label="搜索热议话题"
              />
            </label>
            <button
              type="submit"
              className="whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-primary-600"
            >
              立即围观
            </button>
          </form>

          {/* 热搜词 */}
          <div className="mt-6 flex max-w-xl flex-wrap items-center gap-2">
            <span className="flex items-center gap-1 text-xs font-medium text-foreground-500">
              <i className="ri-fire-fill text-sm text-primary-500" aria-hidden="true"></i>
              实时热词
            </span>
            {hotTags.map((tag, idx) => (
              <button
                key={tag}
                type="button"
                onClick={() => onPickTag(tag)}
                className={
                  idx < 2
                    ? "cursor-pointer rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-900 transition-colors hover:bg-secondary-200"
                    : "cursor-pointer rounded-full bg-background-100 px-3 py-1 text-xs text-foreground-600 ring-1 ring-background-200 transition-colors hover:bg-background-200"
                }
              >
                #{tag}
              </button>
            ))}
          </div>
        </div>

        {/* 右侧围观动态卡 */}
        <aside className="relative z-10 hidden lg:block">
          <div className="rounded-3xl border border-background-200 bg-background-100 p-6">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-bold text-foreground-950">👀 围观群众正在聊</h2>
              <span className="flex items-center gap-1.5 rounded-full bg-accent-100 px-2.5 py-1 text-xs font-medium text-accent-900">
                <span className="h-1.5 w-1.5 rounded-full bg-accent-500"></span> 实时
              </span>
            </div>

            <ul className="mt-4 space-y-3">
              {chatter.map((c) => (
                <li key={c.name} className="flex items-start gap-3 rounded-2xl bg-background-50 p-3 ring-1 ring-background-200">
                  <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-bold ${toneSolid[c.tone]}`}>
                    {c.initial}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="flex items-center gap-1.5 text-xs font-semibold text-foreground-900">
                      {c.name}
                      <span className="text-[11px] font-normal text-foreground-400">{c.time}</span>
                    </p>
                    <p className="mt-0.5 line-clamp-2 text-[13px] leading-snug text-foreground-600">{c.text}</p>
                  </div>
                </li>
              ))}
            </ul>

            <div className="mt-4 flex items-center justify-center gap-2 rounded-2xl bg-primary-500/10 px-4 py-3 text-center">
              <span className="flex -space-x-2">
                {["前", "热", "数", "理"].map((ch, i) => (
                  <span
                    key={i}
                    className={
                      [
                        "bg-primary-500",
                        "bg-secondary-500",
                        "bg-accent-500",
                        "bg-primary-400",
                      ][i] +
                      " flex h-7 w-7 items-center justify-center rounded-full border-2 border-background-50 text-[11px] font-bold text-white"
                    }
                  >
                    {ch}
                  </span>
                ))}
              </span>
              <span className="text-xs text-foreground-600">又有 1,289 位网友加入讨论</span>
            </div>
          </div>
        </aside>
      </div>
    </section>
  );
}